/**
 * Shared mutation hook for recording a retail sale (Task 0.7).
 * Reused by Retail→Каса and any other surface needing a retail fiscal flow.
 */
import { newIdempotencyKey, recordRetailSale, type RetailSaleResult } from '@gym/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from '../../components/ui/Toaster.js';
import { usePosContext } from '../../lib/pos.js';
import { supabase } from '../../lib/supabase.js';

export interface RetailItem {
  product_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  tax_rate_code: 'A' | 'B' | 'C' | 'EXEMPT';
  total: number;
}

export interface RecordRetailSaleArgs {
  items: RetailItem[];
  fiscalize: boolean;
}

/**
 * Returns a TanStack mutation that calls recordRetailSale with a single cash tender
 * for the total of all items.
 *
 * Readiness rule:
 * - fiscalize=true  → requires pos.ready (location + device + staff all present)
 * - fiscalize=false → requires only location + staff (missing device is OK)
 */
export function useRecordRetailSale(onDone?: (r: RetailSaleResult) => void) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pos = usePosContext();

  return useMutation<RetailSaleResult, Error, RecordRetailSaleArgs>({
    mutationFn: async (args: RecordRetailSaleArgs): Promise<RetailSaleResult> => {
      const { items, fiscalize } = args;

      // Readiness check: device only required when fiscalizing
      const missingCritical = fiscalize ? pos.missing : pos.missing.filter((m) => m !== 'device');

      if (missingCritical.length > 0) {
        throw new Error(t('billing.posNotReady'));
      }

      const total = items.reduce((sum, item) => sum + item.total, 0);

      return recordRetailSale(
        supabase,
        {
          tenant_id: pos.tenantId,
          location_id: pos.locationId!,
          member_id: null,
          items,
          tenders: [{ method: 'cash', amount: total }],
          recorded_by_staff_id: pos.staffId!,
          issued_at: new Date().toISOString(),
          ...(fiscalize && pos.fiscalDeviceId ? { fiscal_device_id: pos.fiscalDeviceId } : {}),
          fiscalize,
        },
        newIdempotencyKey(),
      );
    },
    onSuccess: (res) => {
      toast.success(t('retail.saleComplete'));
      // A POS sale changes both inventory and revenue. Prefix-invalidate so all
      // tenant/product variants refresh: Залиха on-hand, product-detail inventory,
      // and the Analytics income list.
      void qc.invalidateQueries({ queryKey: ['low_stock'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['inventory_qty'] });
      void qc.invalidateQueries({ queryKey: ['product_inventory'] });
      void qc.invalidateQueries({ queryKey: ['revenue'] });
      onDone?.(res);
    },
    onError: (e: Error) => {
      toast.error(t('retail.saleFailed'), e.message);
    },
  });
}
